interface IObsArray<T> {

    onChange(callback:(items:T[],eventName:string)=>void);

    offChange(callback:(items:T[],eventName:string)=>void);

    off();

    /**
     * Appends new elements to an array, and returns the new length of the array.
     * @param items New elements of the Array.
     */
    push(...items: T[]): number;

    /**
     * Removes the last element from an array and returns it.
     */
    pop(): T | undefined;

    /**
     * Combines two or more arrays.
     * @param items Additional items to add to the end of array1.
     */
    concat(...items: ConcatArray<T>[]): T[];

    /**
     * Combines two or more arrays.
     * @param items Additional items to add to the end of array1.
     */
    concat(...items: (T | ConcatArray<T>)[]): T[];

    /**
     * Adds all the elements of an array separated by the specified separator string.
     * @param separator A string used to separate one element of an array from the next in the resulting String. If omitted, the array elements are separated with a comma.
     */
    join(separator?: string): string;

    /**
     * Reverses the elements in an Array.
     */
    reverse(): T[];

    /**
     * Removes the first element from an array and returns it.
     */
    shift(): T | undefined;

    /**
     * Sorts an array.
     * @param compareFn Function used to determine the order of the elements. It is expected to return
     * a negative value if first argument is less than second argument, zero if they're equal and a positive
     * value otherwise. If omitted, the elements are sorted in ascending, ASCII character order.
     * ```ts
     * [11,2,22,1].sort((a, b) => a - b)
     * ```
     */
    sort(compareFn?: (a: T, b: T) => number);

    /**
     * Removes elements from an array and, if necessary, inserts new elements in their place, returning the deleted elements.
     * @param start The zero-based location in the array from which to start removing elements.
     * @param deleteCount The number of elements to remove.
     */
    splice(start: number, deleteCount?: number): T[];

    /**
     * Inserts new elements at the start of an array.
     * @param items  Elements to insert at the start of the Array.
     */
    unshift(...items: T[]): number;

    clear();

    clearSilent():IObsArray<T>;

    source():T[];
}

export class ObsArray<T> implements IObsArray<T>{

    private observers:((items: T[], eventName: string)=>void)[]=[];
    private arr:Array<T>;

    notifyChange(eventName:string){
        this.observers.forEach(obs=>obs(this.arr,eventName));
    }

    constructor(arr:Array<T>){
        this.arr = arr;
    }

    onChange(callback: (items: T[], eventName: string) => void) {
        this.observers.push(callback);
    }

    offChange(callback:(items:T[],eventName:string)=>void){
        const i = this.observers.findIndex(it=>it===callback);
        delete this.observers[i];
    }

    off() {
        this.observers = [];
    }

    pop():T{
        const item = this.arr.pop();
        this.notifyChange('pop');
        return item;
    }

    push(...items: T[]): number {
        const i = this.arr.push(...items);
        this.notifyChange('push');
        return i;
    }

    clear() {
        this.arr = [];
        this.notifyChange('clear');
    }

    clearSilent():IObsArray<T>{
        this.arr = []; return this;
    }

    concat(...items: ConcatArray<T>[]): T[];
    concat(...items: (ConcatArray<T> | T)[]): T[];
    concat(...items: (ConcatArray<T> | T)[]): T[] {
        const _items = this.arr.concat(...items);
        this.notifyChange('concat');
        return _items;
    }

    join(separator?: string): string {
        const t = this.arr.join(separator);
        this.notifyChange('join');
        return t;
    }

    reverse(): T[] {
        const t= this.arr.reverse();
        this.notifyChange('reverse');
        return t;
    }

    sort(compareFn?: (a: T, b: T) => number) {
        this.arr.sort(compareFn);
        this.notifyChange('sort');
    }

    splice(start: number, deleteCount?: number): T[] {
        const t = this.arr.splice(start,deleteCount);
        this.notifyChange('splice');
        return t;
    }

    shift(): T {
        const t = this.arr.shift();
        this.notifyChange('shift');
        return t;
    }

    unshift(...items: T[]): number {
        const t =this.arr.unshift(...items);
        this.notifyChange('unshift');
        return t;
    }

    source():T[]{
        return this.arr;
    }

}